import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail, getToken, setToken } from "../lib/api";
import { STORAGE_KEY as LANG_STORAGE_KEY } from "../i18n";

const AuthContext = createContext(null);

function maybeSyncLanguageFromUser(i18n, user) {
  // Only auto-sync from server if the visitor hasn't made an explicit choice yet
  if (!user || !user.language) return;
  let stored = null;
  try {
    stored = localStorage.getItem(LANG_STORAGE_KEY);
  } catch (_) {
    /* ignore */
  }
  if (stored) return; // respect explicit user choice
  if (i18n.resolvedLanguage !== user.language) {
    i18n.changeLanguage(user.language);
  }
}

export function AuthProvider({ children }) {
  // user: null = checking, false = unauthenticated, object = authed
  const [user, setUser] = useState(null);
  const { i18n } = useTranslation();

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(false);
      return null;
    }
    try {
      const { data } = await api.get("/users/me");
      setUser(data);
      maybeSyncLanguageFromUser(i18n, data);
      return data;
    } catch (e) {
      // Invalid/expired token
      setToken(null);
      setUser(false);
      return null;
    }
  }, [i18n]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.access_token);
      setUser(data.user);
      maybeSyncLanguageFromUser(i18n, data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return {
        ok: false,
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
        status: e.response?.status,
      };
    }
  }, [i18n]);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setToken(data.access_token);
      setUser(data.user);
      // For register, the language was a deliberate choice in the form,
      // so always sync (and persist) the selection.
      if (data.user?.language && i18n.resolvedLanguage !== data.user.language) {
        i18n.changeLanguage(data.user.language);
      }
      return { ok: true, user: data.user };
    } catch (e) {
      return {
        ok: false,
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
        status: e.response?.status,
      };
    }
  }, [i18n]);

  const logout = useCallback(() => {
    setToken(null);
    setUser(false);
    // Also purge any wallet cache key so the next visitor doesn't see the
    // previous user's cached balance. Keep AuthContext free of wallet shape
    // knowledge by just removing the known key.
    try {
      localStorage.removeItem("nxa_wallet_cache");
    } catch (_) {
      /* ignore */
    }
  }, []);

  const updateProfile = useCallback(async (patch) => {
    try {
      const { data } = await api.patch("/users/me", patch);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return {
        ok: false,
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      };
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      isLoading: user === null,
      isAuthed: !!user && user !== false,
      login,
      register,
      logout,
      updateProfile,
      refresh,
    }),
    [user, login, register, logout, updateProfile, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
